import { QueryUserCallsCommand, WdaHistoryClient } from '@wildix/wda-history-client';
import { GetCallSummaryCommand, WdaInsightsClient } from '@wildix/wda-insights-client';
import { QueryConversationsCommand, WdaStreamClient } from '@wildix/wda-stream-client';
import { getXBeesToken } from '../xbees/client';

// WDA (Wildix Data Analytics) from the browser with the agent's own x-Bees token (D9). There is
// no shared Wildix key: every request carries the token read from x-Bees just now.

/** The parts of a live call we use (QueryConversations, captured in reference-data/xbees/). */
export interface WdaParty {
  type?: 'LOCAL' | 'REMOTE' | string;
  phone?: string;
  name?: string;
  email?: string;
  userExtension?: string;
}

export interface WdaFlow {
  flowIndex: number;
  startTime?: number;
  status?: string;
  direction?: string;
  caller?: WdaParty;
  callee?: WdaParty;
  remotePhone?: string;
  flags?: string[];
}

export interface WdaLiveCall {
  id: string;
  flows?: WdaFlow[];
}

export interface AgentIdentity {
  extension?: string;
  email?: string;
}

/** The agent's extension and email from the x-Bees token's claims. */
export function identityFromToken(token: string): AgentIdentity {
  try {
    const part = token.split('.')[1] ?? '';
    const claims = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
    const str = (k: string): string | undefined => (typeof claims[k] === 'string' && claims[k] ? (claims[k] as string) : undefined);
    return { extension: str('custom:pbx_user_extension'), email: str('email')?.toLowerCase() };
  } catch {
    return {};
  }
}

export class NoXBeesToken extends Error {
  constructor() {
    super('No x-Bees token: enable "Use x-bees authorization token" on the integration');
    this.name = 'NoXBeesToken';
  }
}

async function requireToken(): Promise<string> {
  const token = await getXBeesToken();
  if (!token) throw new NoXBeesToken();
  return token;
}

const tokenProvider = async (): Promise<{ token: string }> => ({ token: await requireToken() });
const stream = new WdaStreamClient({ token: tokenProvider });
const history = new WdaHistoryClient({ token: tokenProvider });
const insights = new WdaInsightsClient({ token: tokenProvider });

/** The agent's identity and their ongoing calls (filtered by extension server-side). */
export async function fetchOngoing(): Promise<{ me: AgentIdentity; calls: WdaLiveCall[] }> {
  const me = identityFromToken(await requireToken());
  if (!me.extension) return { me, calls: [] };
  const out = await stream.send(new QueryConversationsCommand({ filter: [{ extension: me.extension }] }));
  return { me, calls: (out.calls ?? []) as unknown as WdaLiveCall[] };
}

/** A finished call flow from WDA history (captured): times in epoch ms, durations in seconds. */
export interface WdaHistoryCall {
  id: string;
  flowIndex: number;
  startTime?: number;
  endTime?: number;
  duration?: number;
  talkTime?: number;
  callStatus?: 'COMPLETED' | 'MISSED' | string;
  direction?: string;
  caller?: WdaParty;
  callee?: WdaParty;
  remotePhone?: string;
  flags?: string[];
}

const PAGE_SIZE = 100;
const MAX_PAGES = 10;

/** Reads pages until a short one (or the cap), so a long range isn't cut off at one page. */
export async function collectPages<T>(fetchPage: (offset: number, limit: number) => Promise<T[]>, pageSize = PAGE_SIZE, maxPages = MAX_PAGES): Promise<T[]> {
  const all: T[] = [];
  for (let page = 0; page < maxPages; page++) {
    const rows = await fetchPage(page * pageSize, pageSize);
    all.push(...rows);
    if (rows.length < pageSize) break;
  }
  return all;
}

/** The agent's finished calls since `sinceMs` (epoch ms); the x-Bees token scopes the query to the agent. */
export async function fetchRecentCalls(sinceMs: number): Promise<{ me: AgentIdentity; calls: WdaHistoryCall[] }> {
  const me = identityFromToken(await requireToken());
  const from = new Date(sinceMs);
  const calls = await collectPages(async (offset, limit) => {
    const out = await history.send(new QueryUserCallsCommand({ limit, offset, filter: { from } }));
    return (out.calls ?? []) as unknown as WdaHistoryCall[];
  });
  return { me, calls };
}

export interface CallSummary {
  /** SUCCEEDED = ready; NONE = nothing to summarize (very short call); others = pending/failed. */
  status: string;
  title?: string;
  brief?: string;
}

/** The x-Bees AI summary for a call flow (D5): ready ≤15 s after hangup on a real conversation. */
export async function fetchSummary(callId: string, flowIndex: number): Promise<CallSummary> {
  const out = await insights.send(new GetCallSummaryCommand({ callId, flowIndex }));
  return { status: String(out.status ?? 'NONE'), title: out.summary?.title, brief: out.summary?.brief };
}
